/**
 * ProviderOSWork — incoming work queue.
 *
 * One row per provider_work_item. The first-visit pointer comes LIVE
 * from the placement document on every list — same object the planner
 * reads. Provider users can drill into a row to see paperwork + payouts.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CalendarClock, CheckCircle2, Clock, Loader2, ArrowRight, Inbox,
  Stethoscope, User, AlertTriangle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProviderOSGate from '../components/provider_os/ProviderOSGate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusPill = ({ status }) => {
  const map = {
    opened:               { tone: 'bg-blue-100 text-blue-800',    label: 'Opened' },
    first_visit_locked:   { tone: 'bg-emerald-100 text-emerald-800', label: 'Visit locked' },
    ready_for_payout:     { tone: 'bg-amber-100 text-amber-800',  label: 'Ready for payout' },
    closed:               { tone: 'bg-slate-100 text-slate-700',  label: 'Closed' },
    cancelled:            { tone: 'bg-red-100 text-red-800',      label: 'Cancelled' },
  };
  const m = map[status] || { tone: 'bg-slate-100 text-slate-700', label: status || '—' };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${m.tone}`}>
      {m.label}
    </span>
  );
};

const formatDateTime = (iso) => iso ? new Date(iso).toLocaleString() : '—';

export default function ProviderOSWork() {
  return (
    <ProviderOSGate>
      <WorkInner />
    </ProviderOSGate>
  );
}

function WorkInner() {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/provider/work` + (filter !== 'all' ? `?status=${filter}` : '');
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setRows(res.data.rows || []);
    } catch (e) {
      toast.error('Failed to load work queue');
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="provider-os-work">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900">Incoming work</h1>
          <p className="text-sm text-slate-500 mt-1">
            Accepted placements bound to your provider org.
          </p>
        </div>
        <Link to="/provider-os" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
          Dashboard <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="work-filters">
        {['all', 'opened', 'first_visit_locked', 'ready_for_payout', 'closed', 'cancelled'].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              filter === s
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
            }`}
            data-testid={`filter-${s}`}
          >
            {s === 'all' ? 'All' : s.replace(/_/g, ' ')}
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <Inbox className="w-8 h-8 mx-auto mb-3 text-slate-300" />
            No work items in this view yet.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="work-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-4 py-3 text-left">Placement</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">First visit</th>
                <th className="px-4 py-3 text-left">Urgency</th>
                <th className="px-4 py-3 text-left">Opened</th>
                <th className="px-4 py-3 text-right">Drill</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((w) => {
                const overdue = w.first_visit_at && new Date(w.first_visit_at) < new Date()
                                 && w.placement_status !== 'closed';
                return (
                  <tr key={w.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                      data-testid={`work-row-${w.id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">
                      {w.placement_id.slice(0, 8)}…
                    </td>
                    <td className="px-4 py-3"><StatusPill status={w.status} /></td>
                    <td className="px-4 py-3">
                      {w.first_visit_at ? (
                        <span className={`inline-flex items-center gap-1 ${overdue ? 'text-red-700' : 'text-slate-700'}`}>
                          {overdue ? <AlertTriangle className="w-3.5 h-3.5" /> : <CalendarClock className="w-3.5 h-3.5" />}
                          {formatDateTime(w.first_visit_at)}
                        </span>
                      ) : (
                        <span className="text-slate-400 italic">Not committed</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-slate-700">{w.placement_urgency || '—'}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {formatDateTime(w.opened_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/provider-os/work/${w.id}`}
                        className="text-blue-600 hover:underline text-sm inline-flex items-center gap-1"
                        data-testid={`work-drill-${w.id}`}
                      >
                        Open <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
